import { Badge, Drawer } from '@/ui';
import { DENSITIES, THEMES, type DensityName, type ThemeName } from '@/theme';
import {
  PERSONAS,
  useUiPrefs,
  type PersonaId,
} from '@/store/ui-prefs';

// Right-side drawer with theme / density / persona toggles.
// Persona doubles as the user's role — it drives Home page layout and nav
// visibility. Production will source this from the user record; for now the
// preview switcher lets each user choose what they're focused on.
export function TweaksPanel() {
  const {
    tweaksOpen,
    setTweaksOpen,
    theme,
    setTheme,
    density,
    setDensity,
    persona,
    setPersona,
  } = useUiPrefs();
  const activePersona = PERSONAS.find((p) => p.id === persona);

  return (
    <Drawer open={tweaksOpen} onClose={() => setTweaksOpen(false)} width={380}>
      <div
        style={{
          padding: 'var(--apm-card-pad)',
          borderBottom: '1px solid var(--apm-line)',
        }}
      >
        <div
          style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            color: 'var(--apm-muted)',
            marginBottom: 4,
          }}
        >
          Display preferences
        </div>
        <div
          style={{
            fontFamily: 'var(--apm-font-display)',
            fontSize: 'var(--apm-text-xl)',
            letterSpacing: '-0.01em',
          }}
        >
          Tweak Atlas
        </div>
      </div>

      <div
        style={{
          padding: 'var(--apm-card-pad)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--apm-section-gap)',
          overflowY: 'auto',
        }}
      >
        <Section label="Theme">
          <Pills
            options={THEMES.map((id) => ({ id, label: id }))}
            value={theme}
            onChange={(v) => setTheme(v as ThemeName)}
          />
        </Section>
        <Section label="Density">
          <Pills
            options={DENSITIES.map((id) => ({ id, label: id }))}
            value={density}
            onChange={(v) => setDensity(v as DensityName)}
          />
        </Section>
        <Section label="Role">
          <Pills
            options={PERSONAS.map((p) => ({
              id: p.id,
              label: p.label,
              accessory: p.gatekeeper ? <Badge tone="info" size="sm">Gatekeeper</Badge> : null,
            }))}
            value={persona}
            onChange={(v) => setPersona(v as PersonaId)}
          />
          {activePersona?.hint && (
            <div
              style={{
                marginTop: 10,
                fontSize: 12.5,
                color: 'var(--apm-muted)',
                lineHeight: 1.5,
              }}
            >
              {activePersona.hint}
            </div>
          )}
          <div
            style={{
              marginTop: 10,
              fontSize: 11.5,
              color: 'var(--apm-muted)',
              lineHeight: 1.5,
            }}
          >
            Gatekeeper roles review phase gates. Production will source role
            from your user record; today this picker drives the Home layout.
          </div>
        </Section>
      </div>
    </Drawer>
  );
}

function Section({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: 'var(--apm-muted)',
          fontWeight: 500,
          marginBottom: 10,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

interface PillOption {
  id: string;
  label: string;
  accessory?: React.ReactNode;
}

function Pills({
  options,
  value,
  onChange,
}: {
  options: PillOption[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {options.map((opt) => {
        const active = opt.id === value;
        return (
          <button
            key={opt.id}
            onClick={() => onChange(opt.id)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 30,
              padding: '0 12px',
              background: active ? 'var(--apm-ink)' : 'transparent',
              color: active ? 'var(--apm-bg-2)' : 'var(--apm-ink-2)',
              border: `1px solid ${active ? 'var(--apm-ink)' : 'var(--apm-line-2)'}`,
              borderRadius: 'var(--apm-radius)',
              fontFamily: 'var(--apm-font-body)',
              fontSize: 12.5,
              fontWeight: 500,
              cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {opt.label}
            {opt.accessory}
          </button>
        );
      })}
    </div>
  );
}
